import { ChangeDetectorRef, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormGroup } from '@angular/forms';
import { concat, defer, from, Observable, of, Subject, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  exhaustMap,
  filter,
  finalize,
  map,
  scan,
  shareReplay,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs/operators';
import { CanComponentDeactivate, DeactivationService, DeactivationStatus } from '../deactivation.service';

export enum AutosavingStates {
  Off,
  Saving,
  Saved,
  Done,
  OneOff,
  Failed,
}

/**
 * Per-component autosave lifecycle handle. Returned by `AutosaveService.autoSave()` / `autoSaveCustom()`.
 *
 * Manages debounced autosave, nav-bar save indicator, and deactivation guard integration
 * so components don't need `ngOnDestroy`, `canDeactivate`, or manual state tracking.
 */
export class AutosaveRef implements CanComponentDeactivate {
  readonly isSaving = signal(false);
  readonly lastState = signal(AutosavingStates.Off);

  private _saveSubscription: Subscription | null = null;
  private _autoSaveSubscription: Subscription | null = null;
  private _stopped = false;

  private readonly _executeSave: () => Observable<boolean>;
  private readonly _shouldSave: () => boolean;
  private readonly _canSaveFn: () => boolean;
  private readonly _service: AutosaveService;
  private readonly _deactivationService: DeactivationService;

  /** @internal Created by AutosaveService — do not instantiate directly. */
  constructor(config: {
    executeSave: () => Observable<boolean>;
    trigger: Observable<unknown>;
    shouldSave?: () => boolean;
    service: AutosaveService;
    deactivationService: DeactivationService;
    destroyRef?: DestroyRef;
    canSave?: () => boolean;
    /** @internal Skip DeactivationService registration when the caller handles it (BaseComponent migration). */
    skipDeactivationRegistration?: boolean;
  }) {
    this._executeSave = config.executeSave;
    this._shouldSave = config.shouldSave ?? (() => true);
    this._canSaveFn = config.canSave ?? (() => true);
    this._service = config.service;
    this._deactivationService = config.deactivationService;

    this._service.registerRef(this);

    if (!config.skipDeactivationRegistration) {
      this._deactivationService.subscribe(this);
    }

    this._autoSaveSubscription = config.trigger
      .pipe(
        debounceTime(3000),
        filter(() => !this.isSaving() && !this._stopped && this._canSaveFn() && this._shouldSave()),
        exhaustMap(() => this._performSave())
      )
      .subscribe();

    if (config.destroyRef) {
      config.destroyRef.onDestroy(() => this.stop());
    }
  }

  /** Stop autosaving and unregister from deactivation. Called automatically on DestroyRef destroy. */
  stop(): void {
    if (this._stopped) return;
    this._stopped = true;
    this._saveSubscription?.unsubscribe();
    this._autoSaveSubscription?.unsubscribe();
    this.isSaving.set(false);
    this._service.unregisterRef(this);
    this._deactivationService.unsubscribe(this);
  }

  /** Immediately trigger a save (skips debounce). No-op if already saving or stopped. */
  triggerSave(): void {
    if (this.isSaving() || this._stopped || !this._shouldSave()) return;
    this._saveSubscription?.unsubscribe();
    this._saveSubscription = this._performSave().subscribe();
  }

  /**
   * Called by DeactivationService when the user navigates away.
   * Triggers an actual save attempt and emits state to the nav indicator.
   */
  canDeactivate(): Observable<DeactivationStatus> | DeactivationStatus {
    if (this._stopped) return DeactivationStatus.Accepted;
    if (!this._canSaveFn()) return DeactivationStatus.NeedsConfirmation;

    // Allow navigation if already saving — the HTTP request continues server-side
    // even after unsubscribe. Blocking here would create poor UX for a rare failure case.
    if (this.isSaving()) return DeactivationStatus.Accepted;

    return this._performSave().pipe(map((success) => (success ? DeactivationStatus.Accepted : DeactivationStatus.NeedsConfirmation)));
  }

  /**
   * Core save execution with state management. Used by the debounced pipe (via exhaustMap),
   * triggerSave() (imperative), and canDeactivate() (on-navigate).
   *
   * Sets isSaving/lastState signals synchronously, then delegates to _executeSave().
   * finalize() ensures isSaving is reset even if the subscription is disposed mid-flight.
   */
  private _performSave(): Observable<boolean> {
    return defer(() => {
      this.isSaving.set(true);
      this.lastState.set(AutosavingStates.Saving);
      this._service.emitRefState(this, AutosavingStates.Saving);

      return this._executeSave().pipe(
        take(1),
        timeout({ first: 30_000, with: () => of(false) }),
        tap((success) => {
          const state = success ? AutosavingStates.Saved : AutosavingStates.Failed;
          this.lastState.set(state);
          this._service.emitRefState(this, state);
        }),
        catchError((err) => {
          console.error('Autosave failed:', err);
          this.lastState.set(AutosavingStates.Failed);
          this._service.emitRefState(this, AutosavingStates.Failed);
          return of(false);
        }),
        finalize(() => this.isSaving.set(false))
      );
    });
  }
}

@Injectable({ providedIn: 'root' })
export class AutosaveService {
  private readonly _deactivationService = inject(DeactivationService);
  private readonly _state$ = new Subject<AutosavingStates>();
  private readonly _refStates = new Map<AutosaveRef, AutosavingStates>();

  /** Observable stream of save states (for RxJS consumers, interceptors, existing code). */
  readonly state$: Observable<AutosavingStates> = this._state$.asObservable();

  /** Signal of current save state (for templates, computed state, new code). */
  readonly state = toSignal(this._state$, { initialValue: AutosavingStates.Off });

  /**
   * Display-ready state with transitions for the nav save indicator.
   *
   * Applies the state machine: Saved → (1s) → Done, OneOff → Saved → (1s) → Off.
   * Suppresses Off during Saved→Done transition so component teardown doesn't
   * interrupt the indicator.
   */
  readonly displayState$: Observable<AutosavingStates> = this._state$.pipe(
    scan<AutosavingStates, AutosavingStates>((prev, curr) => {
      if (curr === AutosavingStates.Off && prev === AutosavingStates.Saved) {
        return prev;
      }
      return curr;
    }, AutosavingStates.Off),
    distinctUntilChanged(),
    switchMap((state) => {
      switch (state) {
        case AutosavingStates.Saved:
          return concat(of(AutosavingStates.Saved), of(AutosavingStates.Done).pipe(delay(1000)));
        case AutosavingStates.OneOff:
          return concat(of(AutosavingStates.Saved), of(AutosavingStates.Off).pipe(delay(1000)));
        default:
          return of(state);
      }
    }),
    shareReplay(1)
  );

  /** Signal of display-ready save state (for templates). */
  readonly displayState = toSignal(this.displayState$, { initialValue: AutosavingStates.Off });

  /**
   * Start autosaving a FormGroup. Returns an AutosaveRef for lifecycle control.
   *
   * Handles debounced save on value changes, nav indicator, and deactivation guard.
   * Cleanup is automatic when DestroyRef fires — no ngOnDestroy needed.
   */
  autoSave(config: {
    formGroup: UntypedFormGroup;
    saveFn: (value: any) => Promise<unknown>;
    destroyRef?: DestroyRef;
    changeDetectorRef?: ChangeDetectorRef;
    canSave?: () => boolean;
  }): AutosaveRef {
    const executeSave = (): Observable<boolean> =>
      defer(() => {
        config.changeDetectorRef?.detectChanges();
        if (!config.formGroup.dirty) return of(true);
        return from(config.saveFn(config.formGroup.value)).pipe(
          map(() => true),
          catchError(() => of(false))
        );
      });

    return new AutosaveRef({
      executeSave,
      trigger: config.formGroup.valueChanges,
      shouldSave: () => config.formGroup.dirty,
      service: this,
      deactivationService: this._deactivationService,
      destroyRef: config.destroyRef,
      canSave: config.canSave,
    });
  }

  /**
   * @deprecated Migration-only — do not use in new code. Use {@link autoSave} instead.
   *
   * Bridges existing BaseComponent save/trigger observables during the migration off
   * BaseComponent. Will be removed once all components have migrated to `autoSave()`.
   *
   * Key differences from `autoSave()`:
   * - Accepts pre-built save Observable (cold, emits true/false) instead of a saveFn
   * - Accepts a separate trigger Observable instead of deriving from FormGroup.valueChanges
   * - No `shouldSave` optimization — the save Observable handles dirty checks internally
   *
   * @param config.save A **cold** Observable that emits exactly one `boolean` per subscription
   *   (`true` = success, `false` = failure) and then completes. Hot Observables (`Subject`,
   *   `BehaviorSubject`) are NOT supported — if they don't emit after each re-subscription,
   *   the save will hang until the 30-second timeout fires.
   */
  autoSaveCustom(config: {
    save: Observable<boolean>;
    trigger: Observable<any>;
    destroyRef?: DestroyRef;
    canSave?: () => boolean;
    /** @internal Skip DeactivationService registration when the caller handles it (BaseComponent). */
    skipDeactivationRegistration?: boolean;
  }): AutosaveRef {
    return new AutosaveRef({
      executeSave: () => config.save,
      trigger: config.trigger,
      service: this,
      deactivationService: this._deactivationService,
      destroyRef: config.destroyRef,
      canSave: config.canSave,
      skipDeactivationRegistration: config.skipDeactivationRegistration,
    });
  }

  /** @internal Track a new AutosaveRef for aggregate state computation. */
  registerRef(ref: AutosaveRef): void {
    this._refStates.set(ref, AutosavingStates.Off);
  }

  /** @internal Remove a stopped AutosaveRef and recompute aggregate state. */
  unregisterRef(ref: AutosaveRef): void {
    this._refStates.delete(ref);
    this._emitAggregateState();
  }

  /** @internal Update per-ref state and emit the aggregate to the shared state stream. */
  emitRefState(ref: AutosaveRef, state: AutosavingStates): void {
    if (this._refStates.has(ref)) {
      this._refStates.set(ref, state);
    }
    this._emitAggregateState();
  }

  /** Direct state emission for edge cases (e.g., RetryInterceptor connectivity loss). */
  emit(state: AutosavingStates): void {
    this._state$.next(state);
  }

  /** Compute aggregate state across all active refs: Saving > Failed > Saved > Off. */
  private _emitAggregateState(): void {
    const states = [...this._refStates.values()];
    let aggregate: AutosavingStates;

    if (states.includes(AutosavingStates.Saving)) {
      aggregate = AutosavingStates.Saving;
    } else if (states.includes(AutosavingStates.Failed)) {
      aggregate = AutosavingStates.Failed;
    } else if (states.includes(AutosavingStates.Saved)) {
      aggregate = AutosavingStates.Saved;
    } else {
      aggregate = AutosavingStates.Off;
    }

    this._state$.next(aggregate);
  }
}
